import { IGiveawayAuto, IGiveawayAutoParticipant, IGiveawayAutoWithDetails } from '../../../interfaces';
import privateHttpClient from '../privateHttpClient';
import { urlHttpGov } from '../../urls';
import GiveawayAutoMapper from '../mappers/GiveawayAutoMapper';
import GiveawayAutoParticipantMapper from '../mappers/GiveawayAutoParticipantMapper';

class GiveawaysAutoService {
    private baseURL: string = `${urlHttpGov}/panel/raffle-auto`;

    async listGiveawaysAuto(cityId: number, signal: AbortSignal): Promise<Array<IGiveawayAuto>> {

        const { data } = await privateHttpClient.get(`${this.baseURL}/county/${cityId}`, { signal });

        return data.map((giveaway: any) => GiveawayAutoMapper.toDomain(giveaway));
    }

    async createGiveaway(giveaway: IGiveawayAuto): Promise<IGiveawayAuto> {
        const persistenceGiveaway = GiveawayAutoMapper.toPersistence(giveaway);

        const { data } = await privateHttpClient.post(this.baseURL, persistenceGiveaway);

        const domainGiveaway = GiveawayAutoMapper.toDomain(data);

        return domainGiveaway;
    }

    async getGiveawayById({ id, signal }: { id: number, signal: AbortSignal }): Promise<IGiveawayAuto> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/${id}`, { signal });

        return GiveawayAutoMapper.toDomain(data);
    }

    async updateGiveaway(id: number, giveaway: IGiveawayAuto): Promise<IGiveawayAuto> {
        const persistenceGiveaway = GiveawayAutoMapper.toPersistence(giveaway);

        const { data } = await privateHttpClient.patch(`${this.baseURL}/${id}`, persistenceGiveaway);

        const domainGiveaway = GiveawayAutoMapper.toDomain(data);

        return domainGiveaway;
    }

    deleteGiveaway(id: number) {
        return privateHttpClient.delete(`${this.baseURL}/${id}`);
    }

    async listGiveawaysWithDetails(cityId: number, appUserId: number, signal: AbortSignal): Promise<Array<IGiveawayAutoWithDetails>> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/details/county/${cityId}/app_user/${appUserId}`, { signal });

        return data.map((element: any): IGiveawayAutoWithDetails => ({
            ...GiveawayAutoMapper.toDomain(element.raffle),
            total_tickets: element.count_tickets,
            total_invoices_balance: element.balance_of_invoice,
            total_taxes_balance: element.balance_of_taxe,
        }));
    }

    async exportGiveawayTickets(id: number): Promise<{ hash: string, fileName: string }> {
        const { data } = await privateHttpClient.post(`${this.baseURL}/file`, { raffleId: id });

        return {
            hash: data.hash,
            fileName: data.fileName,
        };
    }

    async downloadGiveawayTicketsFile({ fileName, signal }: { fileName: string, signal: AbortSignal }): Promise<Blob> {
        const response = await privateHttpClient.get(`${this.baseURL}/files/${fileName}`, {
            responseType: 'arraybuffer',
            headers: {
                Accept: 'application/json',
            },
            signal: signal,
        });

        return new Blob([response.data], { type: 'application/json' });
    }

    async uploadGiveawayResult(formData: FormData): Promise<{ hash: string, file: string }> {
        const { data } = await privateHttpClient.post(`${this.baseURL}/generate_winners`, formData, {
            headers: { "Content-Type": 'multipart-formdata' }
        });

        return {
            hash: data.hash_with_winners,
            file: data.file_with_winners,
        };
    }

    async uploadGiveawayFederalLotteryResult({raffleId, formData}: {raffleId: number, formData: FormData}): Promise<IGiveawayAuto> {
        const { data } = await privateHttpClient.post(`${this.baseURL}/generate-winners-federal-lottery`, formData, {
            headers: { "Content-Type": 'multipart-formdata' },
            params: { raffleId },
        });

        return GiveawayAutoMapper.toDomain(data);
    }

    async downloadGiveawayWinnersFile(fileName: string, signal: AbortSignal): Promise<Blob> {
        const response = await privateHttpClient.get(`${this.baseURL}/files/${fileName}`, {
            responseType: 'arraybuffer',
            headers: {
                Accept: 'application/json',
            },
            signal: signal,
        });

        return new Blob([response.data], { type: 'application/json' });
    }

    async listGiveawayParticipantsById({ id, signal }: { id: number, signal: AbortSignal }): Promise<IGiveawayAutoParticipant[]> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/${id}/participants`, { signal });

        return data.map((giveaway: any) => GiveawayAutoParticipantMapper.toDomain(giveaway));
    }

    async getPreviousGiveawayAutoByCurrent({ currentGiveawayAutoId, cityId, signal }: { currentGiveawayAutoId: number, cityId: number, signal: AbortSignal }): Promise<IGiveawayAuto | null> {
        const { data } = await privateHttpClient.get(`${this.baseURL}/previous/raffle/${currentGiveawayAutoId}/county/${cityId}`, { signal });

        if (!data) {
            return null;
        }

        return GiveawayAutoMapper.toDomain(data);
    }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new GiveawaysAutoService();
